/**
 * Pure geometry helpers for the hip spine nomogram.
 *
 * - Coordinate-system conversion for lines
 * - Linear interpolation / projection along a NomogramLine
 * - Safe-zone polygon construction from angle ranges + CSI bounds
 * - Polygon slicing onto the two axes
 * - CSI curve clipping to the safe-zone inclination range
 * - Marker point-in-polygon test
 */

import { convertRadiographicAngles } from '@/domain/anatomy/pelvis/acetabularAngles';
import { type LpsCoordinateSystem } from '@/domain/anatomy/LPS';
import {
    type HipSpineSafeZone,
    type NomogramMarker,
} from './hipSpineNomogramCalculations';
import { type ClippedPoint, type NomogramLine } from './hipSpineNomogramTypes';

/**
 * Convert a nomogram line from one coordinate system to another.
 *
 * Each point is independently converted via 3D vector operations —
 * no sampling or interpolation.
 */
export function convertCupAngles(
    line: NomogramLine,
    sourceCS: LpsCoordinateSystem,
    targetCS: LpsCoordinateSystem,
): NomogramLine {
    const inclinations: number[] = [];
    const anteversions: number[] = [];

    for (let i = 0; i < line.inclination.length; i++) {
        const converted = convertRadiographicAngles(
            line.anteversion[i],
            line.inclination[i],
            sourceCS,
            targetCS,
        );
        inclinations.push(converted.inclination);
        anteversions.push(converted.anteversion);
    }

    return { inclination: inclinations, anteversion: anteversions };
}

/** Find where a segment crosses a boundary on the inclination axis, if at all. */
function interpolateAtInclinationBoundary(
    prevInc: number,
    prevAv: number,
    inc: number,
    av: number,
    boundary: number,
    clampAV: (av: number) => number,
): ClippedPoint | null {
    const straddles =
        (prevInc < boundary && inc > boundary) ||
        (prevInc > boundary && inc < boundary);
    if (!straddles) return null;

    const t = (boundary - prevInc) / (inc - prevInc);
    return {
        inclination: boundary,
        anteversion: clampAV(prevAv + t * (av - prevAv)),
    };
}

/**
 * Clip a CSI line to the zone's inclination range and clamp anteversion.
 *
 * Linearly interpolates at zone boundaries when consecutive line points
 * straddle minInclination or maxInclination, ensuring the clipped output
 * starts and ends at the exact boundary values.
 */
export function clipCSICurveToSafeZone(
    line: NomogramLine,
    zone: HipSpineSafeZone,
    clampAV: (av: number) => number,
): ClippedPoint[] {
    const points: ClippedPoint[] = [];
    const boundaries = [zone.minInclination, zone.maxInclination];

    for (let i = 0; i < line.inclination.length; i++) {
        const inc = line.inclination[i];
        const av = line.anteversion[i];

        if (i > 0) {
            const prevInc = line.inclination[i - 1];
            const prevAv = line.anteversion[i - 1];

            const crossings = boundaries
                .map((b) => interpolateAtInclinationBoundary(prevInc, prevAv, inc, av, b, clampAV))
                .filter((c): c is ClippedPoint => c !== null)
                .sort((a, b) => a.inclination - b.inclination);

            points.push(...crossings);
        }

        if (inc >= zone.minInclination && inc <= zone.maxInclination) {
            points.push({ inclination: inc, anteversion: clampAV(av) });
        }
    }

    return points;
}

/**
 * Linearly interpolate a NomogramLine to find the anteversion at a given inclination.
 * Returns null if the target inclination is outside the line's range.
 */
function interpolateAnteversionAtInclination(line: NomogramLine, targetInc: number): number | null {
    for (let i = 1; i < line.inclination.length; i++) {
        const prevInc = line.inclination[i - 1];
        const currInc = line.inclination[i];
        if (
            (prevInc <= targetInc && currInc >= targetInc) ||
            (prevInc >= targetInc && currInc <= targetInc)
        ) {
            if (prevInc === currInc) return line.anteversion[i];
            const t = (targetInc - prevInc) / (currInc - prevInc);
            return line.anteversion[i - 1] + t * (line.anteversion[i] - line.anteversion[i - 1]);
        }
    }
    return null;
}

/**
 * Public wrapper: interpolate a NomogramLine's anteversion value at a given inclination.
 * Returns null if the line is null or the inclination is outside the line's range.
 */
export function projectAnteversionAtInclination(
    line: NomogramLine | null,
    inclination: number,
): number | null {
    if (!line) return null;
    return interpolateAnteversionAtInclination(line, inclination);
}

/**
 * Find the inclination(s) where a NomogramLine's anteversion equals the target.
 *
 * Walks consecutive line points; for each segment that straddles the target
 * anteversion, linearly interpolates the inclination at the crossing. When
 * the line crosses multiple times (non-monotonic combined-version curves),
 * returns the crossing nearest to `nearestTo` so the slider marker stays
 * close to the surgeon's current inclination. When two crossings are
 * equidistant from `nearestTo`, the one with the lower inclination (earlier
 * along the line) is returned.
 */
export function projectInclinationAtAnteversion(
    line: NomogramLine | null,
    targetAnteversion: number,
    nearestTo: number,
): number | null {
    if (!line) return null;
    const xs = line.inclination;
    const ys = line.anteversion;

    const crossings: number[] = [];
    for (let i = 0; i < xs.length - 1; i++) {
        const y1 = ys[i];
        const y2 = ys[i + 1];
        const straddles =
            (y1 <= targetAnteversion && y2 >= targetAnteversion) ||
            (y1 >= targetAnteversion && y2 <= targetAnteversion);
        if (!straddles) continue;

        const x1 = xs[i];
        const x2 = xs[i + 1];
        if (y1 === y2) {
            crossings.push(x1, x2);
        } else {
            const t = (targetAnteversion - y1) / (y2 - y1);
            crossings.push(x1 + t * (x2 - x1));
        }
    }

    if (crossings.length === 0) return null;
    const uniqueCrossings = [...new Set(crossings)];
    return uniqueCrossings.reduce((best, c) =>
        Math.abs(c - nearestTo) < Math.abs(best - nearestTo) ? c : best,
    );
}

/**
 * Compute the safe-zone polygon as the intersection of the angle range rectangle
 * and the area between the provided CSI upper and lower bound lines.
 *
 * Evaluates both CSI curves at regular inclination steps within the zone,
 * clamps to the anteversion bounds, and builds the polygon only where the
 * clamped upper boundary is above the clamped lower boundary.
 *
 * Returns null if the intersection is empty.
 */
export function computeSafeZonePolygon(
    zone: HipSpineSafeZone,
    upperCSILine: NomogramLine,
    lowerCSILine: NomogramLine,
): { inclination: number[]; anteversion: number[] } | null {
    // Sample both curves at the same inclination points within the zone
    const samplePoints: number[] = [zone.minInclination];
    for (
        let inc = Math.ceil(zone.minInclination);
        inc <= Math.floor(zone.maxInclination);
        inc++
    ) {
        if (inc > zone.minInclination) samplePoints.push(inc);
    }
    if (samplePoints[samplePoints.length - 1] < zone.maxInclination) {
        samplePoints.push(zone.maxInclination);
    }

    // Evaluate and clamp at each sample point
    const upperBoundary: { inclination: number; anteversion: number }[] = [];
    const lowerBoundary: { inclination: number; anteversion: number }[] = [];

    for (const inclination of samplePoints) {
        const upperAnteversion = interpolateAnteversionAtInclination(upperCSILine, inclination);
        const lowerAnteversion = interpolateAnteversionAtInclination(lowerCSILine, inclination);
        if (upperAnteversion === null || lowerAnteversion === null) continue;

        const clampedUpper = Math.min(zone.maxAnteversion, upperAnteversion);
        const clampedLower = Math.max(zone.minAnteversion, lowerAnteversion);

        if (clampedUpper > clampedLower) {
            upperBoundary.push({ inclination, anteversion: clampedUpper });
            lowerBoundary.push({ inclination, anteversion: clampedLower });
        }
    }

    if (upperBoundary.length < 2) return null;

    // Build polygon: upper boundary left-to-right, then lower boundary right-to-left
    const inclination: number[] = [];
    const anteversion: number[] = [];

    for (const pt of upperBoundary) {
        inclination.push(pt.inclination);
        anteversion.push(pt.anteversion);
    }
    for (let i = lowerBoundary.length - 1; i >= 0; i--) {
        inclination.push(lowerBoundary[i].inclination);
        anteversion.push(lowerBoundary[i].anteversion);
    }

    // Close the polygon
    inclination.push(upperBoundary[0].inclination);
    anteversion.push(upperBoundary[0].anteversion);

    return { inclination, anteversion };
}

/**
 * Project the safe-zone polygon onto the anteversion axis at a given inclination.
 *
 * Walks every polygon edge, collects the anteversion values where edges cross
 * the vertical line x = inclination, returns { min, max } of all crossings.
 * For convex polygons this is the natural 1D slice; for concave polygons the
 * outer extent is reported (visually honest for the slider band).
 */
export function projectSafeZonePolygonAtInclination(
    polygon: { inclination: number[]; anteversion: number[] } | null,
    inclination: number,
): { min: number; max: number } | null {
    if (!polygon) return null;
    const xs = polygon.inclination;
    const ys = polygon.anteversion;
    if (xs.length < 3) return null;

    const crossings: number[] = [];
    for (let i = 0; i < xs.length - 1; i++) {
        const x1 = xs[i];
        const x2 = xs[i + 1];
        const straddles =
            (x1 <= inclination && x2 >= inclination) ||
            (x1 >= inclination && x2 <= inclination);
        if (!straddles) continue;

        const y1 = ys[i];
        const y2 = ys[i + 1];
        if (x1 === x2) {
            crossings.push(y1, y2);
        } else {
            const t = (inclination - x1) / (x2 - x1);
            crossings.push(y1 + t * (y2 - y1));
        }
    }

    if (crossings.length === 0) return null;
    return { min: Math.min(...crossings), max: Math.max(...crossings) };
}

/**
 * Project the safe-zone polygon onto the inclination axis at a given anteversion.
 * Mirror of `projectSafeZonePolygonAtInclination`.
 */
export function projectSafeZonePolygonAtAnteversion(
    polygon: { inclination: number[]; anteversion: number[] } | null,
    anteversion: number,
): { min: number; max: number } | null {
    if (!polygon) return null;
    const xs = polygon.inclination;
    const ys = polygon.anteversion;
    if (xs.length < 3) return null;

    const crossings: number[] = [];
    for (let i = 0; i < xs.length - 1; i++) {
        const y1 = ys[i];
        const y2 = ys[i + 1];
        const straddles =
            (y1 <= anteversion && y2 >= anteversion) ||
            (y1 >= anteversion && y2 <= anteversion);
        if (!straddles) continue;

        const x1 = xs[i];
        const x2 = xs[i + 1];
        if (y1 === y2) {
            crossings.push(x1, x2);
        } else {
            const t = (anteversion - y1) / (y2 - y1);
            crossings.push(x1 + t * (x2 - x1));
        }
    }

    if (crossings.length === 0) return null;
    return { min: Math.min(...crossings), max: Math.max(...crossings) };
}

/**
 * Ray-casting point-in-polygon test.
 */
function isPointInPolygon(
    x: number,
    y: number,
    polygon: { inclination: number[]; anteversion: number[] },
): boolean {
    const px = polygon.inclination;
    const py = polygon.anteversion;
    const n = px.length;
    let inside = false;

    for (let i = 0, j = n - 1; i < n; j = i++) {
        if (
            py[i] > y !== py[j] > y &&
            x < ((px[j] - px[i]) * (y - py[i])) / (py[j] - py[i]) + px[i]
        ) {
            inside = !inside;
        }
    }

    return inside;
}

/**
 * Check if a marker falls within the safe zone polygon.
 *
 * The polygon already represents the intersection of the angle range
 * rectangle and the CSI bounds, so a single point-in-polygon test suffices.
 */
export function isMarkerInSafeZonePolygon(
    marker: NomogramMarker | null,
    safeZonePolygon: { inclination: number[]; anteversion: number[] } | null,
): boolean {
    if (!marker || !safeZonePolygon) return false;
    return isPointInPolygon(marker.inclination, marker.anteversion, safeZonePolygon);
}
